import { endOfDay, parseISO } from 'date-fns'
import type { BankingPaymentFilter } from 'generated/resources/graphql'

import { expandStatusFilter } from './statusFilter'

interface DateRange {
  from?: Date | string
  to?: Date | string
}

export interface PaymentsFilters {
  status?: string[]
  currency?: string[]
  account?: string[]
  date?: DateRange
  dueDate?: DateRange
}

/**
 * Converts a Date or string value to a Date object
 */
const toDate = (value: Date | string): Date => {
  if (value instanceof Date) return value
  return parseISO(value)
}

/**
 * Converts a date range to the format expected by the GraphQL filter.
 * Uses 'between' operator for date ranges.
 * The end date is set to end-of-day because due_date is a datetime column
 * in the database, so a bare date would be interpreted as midnight (00:00:00),
 * missing all records during that day.
 */
const getDateRange = (
  dateValue: DateRange | undefined
): { between: [string, string] } | undefined => {
  if (!dateValue?.from || !dateValue?.to) return undefined

  return {
    between: [toDate(dateValue.from).toISOString(), endOfDay(toDate(dateValue.to)).toISOString()],
  }
}

/**
 * Type guard to check if filters object matches PaymentsFilters interface
 */
const isPaymentsFilters = (filters: unknown): filters is PaymentsFilters => {
  if (!filters || typeof filters !== 'object') return false
  return true
}

/**
 * Builds the BankingPaymentFilter object for the GraphQL query
 * Converts UI filter values to the expected GraphQL filter structure
 */
const buildPaymentFilter = (
  filters: PaymentsFilters | undefined
): BankingPaymentFilter | undefined => {
  if (!filters) return undefined

  const filterEntries = Object.entries({
    // StringFilter with 'in' for multi-selection (expand status to include related statuses)
    status: filters.status?.length ? { in: expandStatusFilter(filters.status) } : undefined,
    currency: filters.currency?.length ? { in: filters.currency } : undefined,

    // DateTimeFilter with 'between' for date ranges
    date: getDateRange(filters.date),
    dueDate: getDateRange(filters.dueDate),
  }).filter(([_, value]) => value !== undefined)

  return filterEntries.length > 0 ? Object.fromEntries(filterEntries) : undefined
}

/**
 * Maps DataCollection filter state to GraphQL query variables
 */
export const mapFiltersToVariables = (filters: Record<string, unknown> | undefined) => {
  if (!isPaymentsFilters(filters)) {
    return { accountIds: undefined, filter: undefined }
  }

  return {
    accountIds: filters.account?.length ? filters.account : undefined,
    filter: buildPaymentFilter(filters),
  }
}
